import { ToastSurface, useToastLayer } from './ToastSurface';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ToastContext, ToastInput, ToastRecord } from './useToast';

const DEFAULT_DURATION_MS = 5000;
const MAX_VISIBLE_TOASTS = 4;
const TOAST_DEDUPE_WINDOW_MS = 1600;

const createToastId = () => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `toast-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const zIndex = useToastLayer();
  const [toasts, setToasts] = useState<ToastRecord[]>([]);
  const recentToastKeysRef = useRef(new Map<string, { id: string; shownAt: number }>());

  const dismissToast = useCallback((id: string) => {
    setToasts(current => current.filter(toast => toast.id !== id));
  }, []);

  const showToast = useCallback((input: ToastInput) => {
    const variant = input.variant ?? 'info';
    const now = Date.now();
    const dedupeKey = `${variant}:${input.message}`;
    const recentToast = recentToastKeysRef.current.get(dedupeKey);
    if (recentToast && now - recentToast.shownAt < TOAST_DEDUPE_WINDOW_MS) {
      return recentToast.id;
    }

    const id = createToastId();
    const toast: ToastRecord = {
      id,
      title: input.title,
      message: input.message,
      variant,
      durationMs: input.durationMs ?? (variant === 'error' ? 8000 : DEFAULT_DURATION_MS),
    };
    recentToastKeysRef.current.set(dedupeKey, { id, shownAt: now });
    setToasts(current => [...current, toast].slice(-MAX_VISIBLE_TOASTS));
    return id;
  }, []);

  const value = useMemo(() => ({ showToast, dismissToast }), [dismissToast, showToast]);

  useEffect(() => {
    if (!toasts.length) return;
    const timer = window.setTimeout(() => {
      const cutoff = Date.now() - TOAST_DEDUPE_WINDOW_MS;
      for (const [key, toast] of recentToastKeysRef.current.entries()) {
        if (toast.shownAt < cutoff) recentToastKeysRef.current.delete(key);
      }
    }, TOAST_DEDUPE_WINDOW_MS);
    return () => window.clearTimeout(timer);
  }, [toasts]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {typeof document !== 'undefined' && createPortal(
        <div style={{ zIndex }} className="pointer-events-none fixed inset-x-0 top-4 flex flex-col items-center gap-2 px-3 sm:inset-x-auto sm:right-4 sm:items-end" role="status" aria-live="polite">
          {toasts.map(toast => (
            <div key={toast.id} className="contents"><ToastSurface variant={toast.variant} durationMs={toast.durationMs} onDismiss={() => dismissToast(toast.id)}>
              <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  {toast.title && <div className="text-sm font-semibold">{toast.title}</div>}
                  <div className="text-sm">{toast.message}</div>
                </div>

              </div>
            </ToastSurface></div>
          ))}
        </div>,
        document.body
      )}
    </ToastContext.Provider>
  );
};
